import React, { useEffect, useMemo, useState } from 'react';
import { Typography, Card, Space, Button, Form, Input, Select, Upload, message, List, Divider, Empty, Slider, InputNumber, Row, Col, Avatar, Tag, Checkbox } from 'antd';
import { DownloadOutlined, InboxOutlined, FilterOutlined, DollarOutlined, MailOutlined, PhoneOutlined, EnvironmentOutlined, HomeOutlined, ManOutlined, WomanOutlined, TeamOutlined, HeartOutlined } from '@ant-design/icons';
import '../../Agency/NeighborhoodLens/NeighborhoodLens.css';

const WhiteLabelNeighborhoodLens = () => {
  const { Title, Paragraph, Text } = Typography;
  const [datasets, setDatasets] = useState([]);
  const [loadingDatasets, setLoadingDatasets] = useState(false);
  const [addressForm] = Form.useForm();
  const [addrFileList, setAddrFileList] = useState([]);
  const [filtering, setFiltering] = useState(false);
  const [results, setResults] = useState([]);
  const [radius, setRadius] = useState(1.0); // miles
  const [selectedIds, setSelectedIds] = useState([]);

  const durationOptions = [
    { label: 'Last 7 days', value: '7d' },
    { label: 'Last 14 days', value: '14d' },
    { label: 'Last 30 days', value: '30d' },
    { label: 'Last 90 days', value: '90d' }
  ];

  const loadDatasets = async () => {
    setLoadingDatasets(true);
    try {
      const res = await fetch('/api/audience-datasets');
      if (!res.ok) throw new Error('Failed to load datasets');
      const data = await res.json();
      setDatasets(data);
    } catch (e) {
      message.error(e.message || 'Failed to load datasets');
    } finally {
      setLoadingDatasets(false);
    }
  };

  const quickSetRadius = (val) => setRadius(val);

  const startDemoAnalysis = () => {
    // In real implementation, call backend with target locations + radius + selected dataset
    const mockProfiles = [
      {
        id: 'RJ', initials: 'RJ', name: 'Richard Jackson', age: 48, income: '$100,000 - $150,000',
        marital: 'Single', home: 'Renter', gender: 'Female', children: 'No',
        email: 'richard.jackson@email.com', phone: '(267) 189-6615',
        address: '6463 Main St, Nashville, TN 17916', distance: '0.0 miles', nearest: '2025 Broadway, Nashville, TN 37203'
      },
      {
        id: 'AT', initials: 'AT', name: 'Amanda Thomas', age: 58, income: '$75,000 - $100,000',
        marital: 'Single', home: 'Owner', gender: 'Female', children: 'No',
        email: 'amanda.thomas@email.com', phone: '(645) 743-2195',
        address: '7527 Madison St, Memphis, TN 77015', distance: '0.3 miles', nearest: '1847 Union Ave, Memphis, TN 38104'
      },
      {
        id: 'EM', initials: 'EM', name: 'Elizabeth Miller', age: 60, income: '$75,000 - $100,000',
        marital: 'Married', home: 'Owner', gender: 'Female', children: 'No',
        email: 'elizabeth.miller@email.com', phone: '(816) 854-3407',
        address: '7292 Cedar Ln, Nashville, TN 12475', distance: '0.4 miles', nearest: '2025 Broadway, Nashville, TN 37203'
      }
    ];
    setResults(mockProfiles);
    setSelectedIds([]);
    message.success('Demo analysis complete');
  };

  const exportResults = (data) => {
    const rowsToExport = data ?? results;
    if (!rowsToExport || rowsToExport.length === 0) {
      message.warning('No results to export');
      return;
    }
    const columns = [
      'initials','name','age','income','marital','home','gender','children','email','phone','address','distance','nearest'
    ];
    const header = columns.join(',');
    const escape = (v) => {
      if (v === null || v === undefined) return '';
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const rows = rowsToExport.map(r => columns.map(c => escape(r[c] ?? '')).join(','));
    const csv = [header, ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `neighborhood-lens-results-${new Date().toISOString().slice(0,19).replace(/[:T]/g,'-')}.csv`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  useEffect(() => {
    loadDatasets();
  }, []);

  const uploadAddrProps = useMemo(() => ({
    multiple: false,
    accept: '.csv,.txt',
    beforeUpload: (file) => {
      setAddrFileList([file]);
      return false;
    },
    onRemove: () => setAddrFileList([]),
    fileList: addrFileList
  }), [addrFileList]);

  const handleFilter = async () => {
    try {
      const values = await addressForm.validateFields();
      const { address, duration, pastedAddresses } = values;
      if (!address && addrFileList.length === 0 && !pastedAddresses) {
        message.error('Enter an address, upload a CSV, or paste addresses to continue');
        return;
      }
      setFiltering(true);
      // Placeholder: In the future, send to backend for geocoding/analysis
      const addresses = [];
      if (address) addresses.push(address);
      if (pastedAddresses) addresses.push(...pastedAddresses.split(/\r?\n/).map(s => s.trim()).filter(Boolean));
      if (addrFileList.length) addresses.push(`File: ${addrFileList[0].name}`);
      const mock = addresses.map((a, idx) => ({ id: idx + 1, label: a, duration }));
      await new Promise(r => setTimeout(r, 600));
      setResults(mock);
      message.success('Filtered results ready');
    } catch (e) {
      if (e?.errorFields) return;
      message.error(e.message || 'Filter failed');
    } finally {
      setFiltering(false);
    }
  };

  return (
    <div className="neighborhood-lens-page">
      <Title level={3}>NeighborhoodLens</Title>

      <Card title="Admin Datasets" style={{ marginBottom: 16 }}>
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Download the dataset uploaded by admin earlier.
        </Paragraph>
        <List
          loading={loadingDatasets}
          locale={{ emptyText: <Empty description="No datasets yet" /> }}
          dataSource={datasets}
          renderItem={(item) => (
            <List.Item
              actions={[
                <a key="download" href={item.path} download>
                  <Button type="link" icon={<DownloadOutlined />}>Download</Button>
                </a>
              ]}
            >
              <List.Item.Meta
                title={<span>{item.name} <Text type="secondary">({item.originalName})</Text></span>}
                description={<Text type="secondary">Uploaded {new Date(item.uploadedAt).toLocaleString()}</Text>}
              />
            </List.Item>
          )}
        />
      </Card>

      <Card title="1. Target Locations" style={{ marginBottom: 16 }}>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Title level={5} style={{ margin: 0 }}>Select or add the locations you want to analyze around</Title>
          <Paragraph type="secondary" style={{ marginBottom: 0 }}>Add Custom Location</Paragraph>
          <Form form={addressForm} layout="vertical">
            <Form.Item label="Address" name="address">
              <Input placeholder="Enter any address you want to target" allowClear />
            </Form.Item>
            <Form.Item label="Duration" name="duration" initialValue="30d">
              <Select options={durationOptions} style={{ maxWidth: 240 }} />
            </Form.Item>
            <Divider plain>Or</Divider>
            <Form.Item label="Upload CSV of addresses" tooltip="One address per line or with a header column">
              <Upload.Dragger {...uploadAddrProps}>
                <p className="ant-upload-drag-icon"><InboxOutlined /></p>
                <p className="ant-upload-text">Click or drag file to this area to upload</p>
                <p className="ant-upload-hint">CSV or TXT</p>
              </Upload.Dragger>
            </Form.Item>
            <Divider plain>Or</Divider>
            <Form.Item label="Paste addresses (one per line)" name="pastedAddresses">
              <Input.TextArea rows={4} placeholder={"123 Main St, City, ST 00000\n456 Elm Ave, City, ST 00000"} />
            </Form.Item>
            <Button type="primary" icon={<FilterOutlined />} onClick={handleFilter} loading={filtering}>Filter</Button>
          </Form>
        </Space>
      </Card>

      <Card title="2. Search Radius" style={{ marginBottom: 16 }}>
        <Paragraph>Configure the search distance around your target locations.</Paragraph>
        <Space direction="vertical" size={8} style={{ width: '100%' }}>
          <Text strong>Selected Target Locations then Search Radius</Text>
          <div className="map-placeholder">
            <Text type="secondary">
              Visual representation of search coverage area — for demonstration purposes only. Map does not reflect actual locations, addresses, or real-world data.
            </Text>
          </div>
          <Text strong>Quick Select</Text>
          <Space wrap>
            {[0.5, 1, 2.5, 5, 10, 20].map(v => (
              <Button key={v} onClick={() => quickSetRadius(v)} type={radius === v ? 'primary' : 'default'}>{v} mi</Button>
            ))}
          </Space>
          <Row gutter={16} align="middle">
            <Col xs={24} md={16}>
              <Slider min={0.5} max={50} step={0.5} value={radius} onChange={setRadius} tooltip={{ formatter: (v) => `${v} mi` }} />
            </Col>
            <Col xs={24} md={8}>
              <Space>
                <Text>Custom Radius:</Text>
                <InputNumber min={0.5} max={50} step={0.5} value={radius} onChange={setRadius} />
                <Text>miles</Text>
              </Space>
            </Col>
          </Row>
          <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button type="primary" onClick={startDemoAnalysis}>Start Demo Analysis</Button>
          </div>
        </Space>
      </Card>

      <Card title="Results">
        {results.length === 0 ? (
          <Empty description="Run a filter to see matched addresses" />
        ) : (
          <>
            <div className="results-toolbar">
              <Space wrap>
                <Button>Filter & Export</Button>
                <Input placeholder="Search Profiles" style={{ width: 220 }} allowClear />
                <Tag>Max Distance: {radius} miles</Tag>
              </Space>
              <Space wrap>
                <Button onClick={() => setSelectedIds(results.map(r => r.id))}>Select Page</Button>
                <Button onClick={() => setSelectedIds([])}>Clear All</Button>
                <Button onClick={() => exportResults(results.filter(r => selectedIds.includes(r.id)))} disabled={!selectedIds.length}>Export Selected</Button>
                <Button type="primary" onClick={() => exportResults(results)} disabled={!results.length}>Export All</Button>
                <Text type="secondary">Selected: {selectedIds.length} • {results.length} of {results.length} shown</Text>
              </Space>
            </div>
            <List
              grid={{ gutter: 16, column: 3 }}
              dataSource={results}
              renderItem={(p) => (
                <List.Item>
                  <Card size="small" className="profile-card" bordered>
                    <div className="profile-card-top">
                      <Checkbox
                        checked={selectedIds.includes(p.id)}
                        onChange={(e) => {
                          setSelectedIds((prev) => {
                            const set = new Set(prev);
                            if (e.target.checked) set.add(p.id); else set.delete(p.id);
                            return Array.from(set);
                          });
                        }}
                      />
                      <Avatar className="profile-avatar">{p.initials}</Avatar>
                      <div className="profile-title">
                        <div className="profile-name">{p.name}</div>
                        <div className="profile-sub">Age: {p.age}</div>
                      </div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-meta-grid">
                      <div className="meta-item"><DollarOutlined /> <span>{p.income}</span></div>
                      <div className="meta-item"><HeartOutlined /> <span>{p.marital}</span></div>
                      <div className="meta-item"><HomeOutlined /> <span>{p.home}</span></div>
                      <div className="meta-item">{p.gender === 'Female' ? <WomanOutlined /> : <ManOutlined />} <span>{p.gender}</span></div>
                      <div className="meta-item"><TeamOutlined /> <span>Children: {p.children}</span></div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-meta-grid">
                      <div className="meta-item full"><MailOutlined /> <span>{p.email}</span></div>
                      <div className="meta-item full"><PhoneOutlined /> <span>{p.phone}</span></div>
                      <div className="meta-item full"><EnvironmentOutlined /> <span>{p.address}</span></div>
                    </div>
                    <Divider className="profile-divider" />
                    <div className="profile-footer">
                      <span className="distance-link">{p.distance} away</span>
                      <div className="nearest"><Text type="secondary">Nearest target:</Text> {p.nearest}</div>
                    </div>
                  </Card>
                </List.Item>
              )}
            />
          </>
        )}
      </Card>
    </div>
  );
};

export default WhiteLabelNeighborhoodLens;
